// UIHandlers.js
import { StairStateManager } from "./StairStateManager";

export default class UIHandlers {
    constructor(mainSceneSetup) {
        this.mainSceneSetup = mainSceneSetup; // Reference to MainSceneSetup to control staircase loading
        this.stairState = new StairStateManager(mainSceneSetup);
    }

    initFormAnimations() {
        const formFields = document.querySelectorAll('.form-field input');
        
        formFields.forEach(input => {
            // Check initial state (in case of autofill)
            if (input.value) {
                input.parentElement.classList.add('has-value');
            }
    
            // Handle input changes
            input.addEventListener('input', () => {
                if (input.value) {
                    input.parentElement.classList.add('has-value');
                } else {
                    input.parentElement.classList.remove('has-value');
                }
            });
    
            // Handle focus
            input.addEventListener('focus', () => {
                input.parentElement.classList.add('is-focused');
            });
    
            // Handle blur
            input.addEventListener('blur', () => {
                input.parentElement.classList.remove('is-focused');
                if (!input.value) {
                    input.parentElement.classList.remove('has-value');
                }
            });
        });
    }
    // Attach all the UI event listeners
    init() {
        // Attachment type change handler
        document.querySelectorAll('input[name="cantilever-option"]').forEach((radio) => {
            radio.addEventListener('change', (event) => {
                const attachmentType = event.target.value;
                this.stairState.handleAttachmentChange(attachmentType);
                this.updateConfigurationVisibility(attachmentType);
                this.updateUIElementsVisibility(attachmentType);
                this.setDefaultConfiguration(attachmentType);
            });
        });

        // Stair type change handler
        document.querySelectorAll('input[name="stair-type-selector"]').forEach((radio) => {
            radio.addEventListener('change', async (event) => {
                const stairType = event.target.value;
                await this.stairState.handleTypeChange(stairType);
                this.updateSliderVisibility(stairType);
            });
        });


        // Initialize visibility and default selection based on default attachment type
        const defaultAttachmentTypeRadio = document.querySelector('input[name="cantilever-option"]:checked');
        if (defaultAttachmentTypeRadio) {
            const attachmentType = defaultAttachmentTypeRadio.value;
            this.updateConfigurationVisibility(attachmentType);
            this.updateUIElementsVisibility(attachmentType);
            this.setDefaultConfiguration(attachmentType);
        }

        // Bottom Flight Slider (for L-shaped and U-shaped stairs)
        const bottomFlightSlider = document.getElementById('bottom-flight-slider');
        const bottomFlightInput = document.getElementById('bottom-flight-input');
        bottomFlightSlider.addEventListener('input', (event) => {
            const bottomTreads = parseInt(event.target.value, 10);
            bottomFlightInput.value = bottomTreads; // Sync input with slider
            const topTreads = parseInt(document.getElementById('top-flight-slider').value, 10);
            this.mainSceneSetup.currentStaircase.updateStairTreads(bottomTreads, topTreads);  // Update both flights
        });
        bottomFlightInput.addEventListener('input', (event) => {
            const bottomTreads = parseInt(event.target.value, 10);
            bottomFlightSlider.value = bottomTreads; // Sync slider with input
            const topTreads = parseInt(document.getElementById('top-flight-slider').value, 10);
            this.mainSceneSetup.currentStaircase.updateStairTreads(bottomTreads, topTreads);  // Update both flights
        });

        // Top Flight Slider (for L-shaped and U-shaped stairs)
        const topFlightSlider = document.getElementById('top-flight-slider');
        const topFlightInput = document.getElementById('top-flight-input');
        topFlightSlider.addEventListener('input', (event) => {
            const topTreads = parseInt(event.target.value, 10);
            topFlightInput.value = topTreads; // Sync input with slider
            const bottomTreads = parseInt(document.getElementById('bottom-flight-slider').value, 10);
            this.mainSceneSetup.currentStaircase.updateStairTreads(bottomTreads, topTreads);  // Update both flights
        });
        topFlightInput.addEventListener('input', (event) => {
            const topTreads = parseInt(event.target.value, 10);
            topFlightSlider.value = topTreads; // Sync slider with input
            const bottomTreads = parseInt(document.getElementById('bottom-flight-slider').value, 10);
            this.mainSceneSetup.currentStaircase.updateStairTreads(bottomTreads, topTreads);  // Update both flights
        });

        // Width Slider
        document.getElementById('widthSlider').addEventListener('input', (event) => {
            const width = parseInt(event.target.value, 10);
            this.mainSceneSetup.currentStaircase.updateTreadWidth(width);
            document.getElementById('widthInput').value = width; // Sync with number input
        });

        // Width Number Input
        document.getElementById('widthInput').addEventListener('input', (event) => {
            const width = parseInt(event.target.value, 10);
            this.mainSceneSetup.currentStaircase.updateTreadWidth(width);
            document.getElementById('widthSlider').value = width; // Sync with slider
        });

        // Material Selection
        document.querySelectorAll('input[name="materialRadio"]').forEach((radio) => {
            radio.addEventListener('change', () => {
                const selectedMaterial = document.querySelector('input[name="materialRadio"]:checked').value;
                if (selectedMaterial === "No Tread") {
                    this.mainSceneSetup.currentStaircase.updateMaterial("none");
                } else if (selectedMaterial === "American Ash") {
                    this.mainSceneSetup.currentStaircase.updateMaterial('../textures/oak_veneer_01_diff_4k.jpg');
                } else if (selectedMaterial === "American Oak") {
                    this.mainSceneSetup.currentStaircase.updateMaterial('../textures/oak_veneer_01_diff_4k.jpg');
                }
            });
        });
        this.initFormAnimations();
    }

    setDefaultConfiguration(attachmentType) {
        let defaultConfig;

        if (attachmentType === 'Spiral') {
            defaultConfig = 'Basic-Spiral';
        } else {
            // Default to 'straight' for both 'central-stringer' and 'cantilever'
            defaultConfig = 'straight';
        }

        // Set the default configuration radio button
        const defaultConfigRadio = document.querySelector(`input[name="stair-type-selector"][value="${defaultConfig}"]`);
        if (defaultConfigRadio) {
            defaultConfigRadio.checked = true;
            // Dispatch change event to trigger model loading
            const event = new Event('change');
            defaultConfigRadio.dispatchEvent(event);
        }
    }

    updateConfigurationVisibility(attachmentType) {
        const standardConfigs = ['straight', 'L-shaped', 'U-shaped'];
        const spiralConfigs = ['Basic-Spiral', 'Bespoke-Spiral'];

        document.querySelectorAll('input[name="stair-type-selector"]').forEach((radio) => {
            const radioLabel = radio.closest('.radio-inline');
            if (attachmentType === 'Spiral') {
                // Show only spiral configurations
                radioLabel.style.display = spiralConfigs.includes(radio.value) ? '' : 'none';
            } else {
                // Show only standard configurations
                radioLabel.style.display = standardConfigs.includes(radio.value) ? '' : 'none';
            }
        });
    }

    updateUIElementsVisibility(attachmentType) {
        // Handle width controls visibility
        const widthSection = document.getElementById('widthSliderWrapper');
        if (widthSection) {
            widthSection.style.display = attachmentType === 'Spiral' ? 'none' : '';
        }
    
        // Handle material selection visibility
        const materialContainer = document.getElementById('tread-material');
        if (materialContainer) {
            materialContainer.style.display = attachmentType === 'Spiral' ? 'none' : '';
        }
    }

    updateSliderVisibility(stairType) {
        if (stairType === 'L-shaped' || stairType === 'U-shaped') {
            document.getElementById('bottom-flight-slider-container').style.display = 'block';
            document.getElementById('top-flight-slider-container').style.display = 'block';
            document.getElementById('stair-slider-container').style.display = 'none';
        } else {
            document.getElementById('bottom-flight-slider-container').style.display = 'none';
            document.getElementById('top-flight-slider-container').style.display = 'none';
            document.getElementById('stair-slider-container').style.display = 'block';
        }
    }
}
